import { c as _c } from "react/compiler-runtime";
import { AnalyticsBarChart } from "@components/analytics/in-house/analytics-bar-chart";
import { CHART_HEIGHT } from "@components/analytics/in-house/constants/analytics-constants";
import { Image360NoImageIcon, NO_TRANSLATE_CLASS, Skeleton, Text } from "@flat-ui";
import { useFetchCapturedData } from "@hooks/captured-data/use-fetch-captured-data";
import { useDateTime } from "@hooks/use-date-time";
import { Box, Stack } from "@mui-wrapper";
import { CapturedDataSummaryFilters } from "@pages/project-details/project-overview/captured-data-summary/captured-data-summary-filters";
import { CapturedDataSummaryStats } from "@pages/project-details/project-overview/captured-data-summary/captured-data-summary-stats";
import { ALL_AREAS, filterCaptures, getCaptureStats, getCapturesPerArea } from "@pages/project-details/project-overview/captured-data-summary/captured-data-summary-utils";
import { MarkupsSummaryEmpty } from "@pages/project-details/project-overview/markups-summary/markups-summary-empty";
import { capturedDataFetchingStatusSelector, capturedDataSelector } from "@store/captured-data/captured-data-selector";
import { useAppSelector } from "@store/store-helper";
import { useState } from "react";
/** Amounts of photos, when the latest was taken and, for several areas, the photos per area */
function CapturedDataSummaryView(t0) {
  const $ = _c(25);
  const {
    captures,
    isAreaSelected
  } = t0;
  const {
    areas
  } = useAppSelector(capturedDataSelector);
  const {
    formatDate
  } = useDateTime();
  let T0;
  let capturesPerArea;
  let stats;
  let t1;
  let t2;
  let t3;
  if ($[0] !== areas || $[1] !== captures || $[2] !== formatDate) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      stats = getCaptureStats(captures);
      let t4;
      if ($[9] !== areas || $[10] !== captures) {
        t4 = getCapturesPerArea(captures, areas);
        $[9] = areas;
        $[10] = captures;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      capturesPerArea = t4;
      if (!captures.length) {
        let t5;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <MarkupsSummaryEmpty title={"No 360\xB0 photos"} icon={<Image360NoImageIcon sx={{
            width: "2.5rem",
            height: "2.5rem"
          }} />} message={"There are no 360\xB0 photos that match the selected filters."} />;
          $[12] = t5;
        } else {
          t5 = $[12];
        }
        t3 = t5;
        break bb0;
      }
      T0 = Stack;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          gap: 2
        };
        $[13] = t1;
      } else {
        t1 = $[13];
      }
      t2 = stats.lastCapturedAt && <Text variant="bodyM" sx={{
        color: "text.secondary"
      }}>Last captured: <var>{formatDate(stats.lastCapturedAt)}</var></Text>;
    }
    $[0] = areas;
    $[1] = captures;
    $[2] = formatDate;
    $[3] = T0;
    $[4] = capturesPerArea;
    $[5] = stats;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    T0 = $[3];
    capturesPerArea = $[4];
    stats = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[14] !== stats) {
    t4 = <CapturedDataSummaryStats stats={stats} />;
    $[14] = stats;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== capturesPerArea || $[17] !== isAreaSelected) {
    t5 = !isAreaSelected && capturesPerArea.length > 1 && <Box className={NO_TRANSLATE_CLASS}><AnalyticsBarChart dataset={capturesPerArea} xTickStep={1} yAxisLabel={"360\xB0 photos"} seriesUnitLabel="photos" /></Box>;
    $[16] = capturesPerArea;
    $[17] = isAreaSelected;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== T0 || $[20] !== t1 || $[21] !== t2 || $[22] !== t4 || $[23] !== t5) {
    t6 = <T0 sx={t1}>{t2}{t4}{t5}</T0>;
    $[19] = T0;
    $[20] = t1;
    $[21] = t2;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
}

/**
 * Summary of the 360 photos of the project, taken on their own, in a time series or with VideoMode,
 * from the Project API. It only shows the current data, filtered by area and by capture date.
 */
export function CapturedDataSummary(t0) {
  const $ = _c(29);
  const {
    projectId
  } = t0;
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useFetchCapturedData(t1);
  const {
    captures,
    areas
  } = useAppSelector(capturedDataSelector);
  const fetchingStatus = useAppSelector(capturedDataFetchingStatusSelector);
  const [areaId, setAreaId] = useState(ALL_AREAS);
  const [dateFilter, setDateFilter] = useState("anyTime");
  const isLoading = fetchingStatus === "uninitialized" || fetchingStatus === "pending";
  let t2;
  if ($[2] !== areaId || $[3] !== captures || $[4] !== dateFilter) {
    t2 = filterCaptures(captures, {
      areaId,
      dateFilter,
      now: Date.now()
    });
    $[2] = areaId;
    $[3] = captures;
    $[4] = dateFilter;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const filteredCaptures = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      gap: 2
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1
    };
    t5 = <Text variant="heading16">Captured data</Text>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== areaId || $[10] !== areas || $[11] !== dateFilter || $[12] !== fetchingStatus) {
    t6 = fetchingStatus === "succeeded" && <CapturedDataSummaryFilters areas={areas} areaId={areaId} dateFilter={dateFilter} onAreaChange={setAreaId} onDateFilterChange={setDateFilter} />;
    $[9] = areaId;
    $[10] = areas;
    $[11] = dateFilter;
    $[12] = fetchingStatus;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <Stack direction="row" sx={t4}>{t5}{t6}</Stack>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isLoading) {
    t8 = isLoading && <Skeleton variant="rectangular" width="100%" height={CHART_HEIGHT} />;
    $[16] = isLoading;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== fetchingStatus) {
    t9 = fetchingStatus === "rejected" && <MarkupsSummaryEmpty isError={true} message={"Failed to fetch the project 360\xB0 photos. Please reload the page and try again."} />;
    $[18] = fetchingStatus;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== areaId || $[21] !== fetchingStatus || $[22] !== filteredCaptures) {
    t10 = fetchingStatus === "succeeded" && <CapturedDataSummaryView captures={filteredCaptures} isAreaSelected={areaId !== ALL_AREAS} />;
    $[20] = areaId;
    $[21] = fetchingStatus;
    $[22] = filteredCaptures;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t11 = <Stack sx={t3}>{t7}{t8}{t9}{t10}</Stack>;
    $[24] = t10;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}